import Backbone from 'backbone'
import template from './alert-form.hbs'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import MultiPrincipalChooser from 'scoreboard/components/multi-principal-chooser'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import FormView from 'scoreboard/components/form-view'
import formHelper from 'scoreboard/components/form-helper'

let potentialRecipients

/**
 * Form for creating an alert
 * Used by  Admin | Broadcast Alert and Forms "Send Alert" Action
 * (AlertDetails dialog)
 */
const AlertForm = Backbone.View.extend({
	tagName: 'form',
	className: 'alert-form controls',
	events: {
		'click .cancel': 'clearForm'
	},
	/**
	 * @param {Object} options
	 * @param {FormView} [options.formView] the FormView
	 * @param {String} [options.url] the endpoint to use to retrieve the principal list
	 * @param {Boolean} [options.displaySubmitOptions=false] if the submit options
	 * should be displayed
	 * @param {Function} [options.onSubmit] function to call when the form is submitted
	 * (formData) => {}
	 * @param {Object} [options.primaryButtonEl] selector for the primary button
	 * if it is outside the form (e.g. in a dialog)
	 * @param {Boolean} [options.popup = false] if the alert should be sent as a popup
	 * @param {String} [options.message] the alert message, if displaying a saved alert
	 * @param {Principal[]} [options.recipients] currently selected
	 * recipients of this alert, if displaying a saved alert or toggling between
	 * creating and editing a form action before saving
	 */
	initialize: function(options = {}) {
		this.options = _.extend({
			popup: false
		}, options)
		this.formView = new FormView({
			el: this.el,
			guardOptions: this.options.guardOptions ?? {},
			submit: () => this.submit(),
			primaryButtonEl: this.options.primaryButtonEl
		})
	},

	render: function() {
		this.$el.empty().append(template(this.context()))
		formHelper.writeToDom(this.options, this.el)
		this.renderPrincipalChooser()
		this.markFields()
		this.formView.start()
		return this
	},

	context: function() {
		return {
			displaySubmitOptions: this.options.displaySubmitOptions ?? false
		}
	},

	renderPrincipalChooser: function() {
		let url = this.options.url ?? 'api/alerts/recipients'
		const optionDecoratorFn = option => {
			if (option.type === 'group') {
				if (option.id > 0) {
					option.group = i18n.get('common.group.groups')
				} else {
					option.group = 'headerless'
				}
			}
			if (option.type === 'user') {
				option.group = i18n.get('common.user.users')
			}
		}
		this.principalChooser = new MultiPrincipalChooser({
			el: this.$('.recipient-chooser'),
			url,
			cache: potentialRecipients,
			placeholder: i18n.get('alerts.selectRecipients'),
			multiple: true,
			autofocus: false,
			groupBy: 'group',
			groupSortOrder: ['user', 'group'],
			optionDecoratorFn,
			currentPrincipals: this.options?.recipients
		}).render()
		this.principalChooser.on('load', function(data) {
			potentialRecipients = data
		})
		this.principalChooser.show({open: false})
	},

	markFields: function() {
		this.markedFields = []
		this.markedFields.push(new MarkedField({
			el: this.$('.message'),
			validator: requiredValidator
		}).render())

		this.markedFields.push(new MarkedField({
			el: this.$('select.recipients'),
			validator: requiredValidator
		}).render())
	},

	clearForm: function() {
		formHelper.writeToDom({}, this.el, true)
		this.principalChooser.reset()
		this.principalChooser.trigger('load', potentialRecipients)
		this.$('input[name=popup').prop('checked', false)
		this.formView.start()
	},

	gatherData: function() {
		const data = {
			userIds: [],
			groupIds: [],
			...formHelper.readFromDom(this.el)
		}
		let recipients = this.principalChooser._selectedPrincipals()
		data.recipients = recipients
		_.each(recipients, recipient => {
			if (recipient.type === 'user') {
				data.userIds.push(recipient.id)
			} else {
				data.groupIds.push(recipient.id)
			}
		})
		return data
	},

	submit: function() {
		const data = this.gatherData()
		// if this.options.onSubmit is defined, call it with the data
		if (!_.isNil(this.options.onSubmit)) {
			this.options.onSubmit(data)
		}
	},

	autoRemove: true

})

export default AlertForm
AlertForm.__filename__ = 'admin/alerts/alert-form.js'
